import { useCallback, useContext, useRef, useState } from 'react';
import { AdyenComponentContainer } from '@/components/expressPayment/AdyenComponentContainer';
import { useAdyenApplePay } from './useAdyenApplePay';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { ApplePayButtonPlaceholder } from '@/components/Wallets/WalletButton/ApplePayButtonPlaceholder';
import { ApplePayTabFallback } from '@/components/Wallets/ApplePayTabFallback/ApplePayTabFallback';
import { useApplePayTabFallbackGate } from '@/hooks/useApplePayTabFallbackGate';
import { armApplePayTabFallbackIfEligible } from '@/utils/applePayTabFallback';
import { EXPRESS_WALLET_NAME, WALLET_AVAILABILITY_REASON } from '@/utils/adyenWalletAvailability';

export const AdyenApplePay = (props: ExpressCheckoutComponentProps) => {
  // iOS <= 16 cross-origin iframe (Adyen's component can never become
  // available without `ApplePaySession`), or Apple already rejected this
  // page's merchant domain. Render the new-tab fallback instead — it
  // reports `onReady(true)` so templates don't swap away.
  if (useApplePayTabFallbackGate()) {
    return <ApplePayTabFallback {...props} />;
  }
  return <AdyenApplePayWallet {...props} />;
};

const AdyenApplePayWallet = ({ onReady }: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const { featureFlags } = context;
  const reportWalletAvailability = useExpressWalletFallback();
  const [state, setState] = useState<WalletReadyState>('loading');
  const mountTimeRef = useRef(Date.now());

  const onClick = useCallback(
    (resolve: () => void) => {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
      resolve();
    },
    [context]
  );

  const handleReady = useCallback(
    (isAvailable: boolean, reason?: string) => {
      // Domain not supported in this frame: arm the fake new-tab button
      // instead of collapsing. Parent remounts as ApplePayTabFallback.
      if (!isAvailable && armApplePayTabFallbackIfEligible(featureFlags)) return;
      reportWalletAvailability(isAvailable, {
        wallet: EXPRESS_WALLET_NAME.APPLE_PAY,
        provider: ProviderEnum.ADYEN,
        reason: isAvailable ? undefined : (reason ?? WALLET_AVAILABILITY_REASON.UNKNOWN),
        loadTimeMs: Date.now() - mountTimeRef.current,
      });
      setState(isAvailable ? 'ready' : 'unavailable');
      onReady?.(isAvailable, reason);
    },
    [onReady, reportWalletAvailability, featureFlags]
  );

  const { applePayElement } = useAdyenApplePay({ onReady: handleReady, onClick });

  // Always go through the visual-proxy shell so Adyen Apple Pay renders the
  // exact same 44px / `rounded-lg` branded placeholder as every other express
  // wallet (Stripe Apple/Google Pay, Adyen Google Pay, PayPal). Previously the
  // DEFAULT-template / non-iOS branch returned a bare `<apple-pay-button>`
  // mounted OUTSIDE `.wallet-btn`, so it missed the `--apple-pay-button-height`
  // pin and our placeholder — the browser painted the web component at its
  // ~30px default height with its own corners (the "tiny, square" Apple Pay on
  // macOS Safari). The shell collapses to 0px when the wallet reports
  // unavailable, so devices without Apple Pay still don't keep a reserved slot.
  return (
    <WalletButtonShell
      state={state}
      placeholder={<ApplePayButtonPlaceholder />}
      testId="wallet-btn-apple"
      usePlaceholderAsVisual
    >
      <AdyenComponentContainer element={applePayElement} />
    </WalletButtonShell>
  );
};
